import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccountLink,
  CommsAllowance,
  ConnectedAccount,
  CreateAccountLink,
} from '@salonomy/types';
import { api, ApiClientError, unwrap } from '../lib/api.js';

/**
 * Integration health surfaces for the WS6 developer panel: Stripe Connect onboarding status (+ a
 * re-onboarding link) and comms-provider health (the SMS/WhatsApp allowance meter). Owner/manager
 * only — mirrors the API requireManager guard. Read-only status here; the panel never takes money
 * or sends a message, it just shows whether the integrations are live and links out to finish setup.
 *
 * Own cache namespace (keys local to this file) so the developer panel doesn't collide with the
 * comms/payments surfaces elsewhere in the console.
 */
const ik = {
  connectAccount: ['connect-account'] as const,
  commsAllowance: ['comms-allowance'] as const,
};

/**
 * The salon's Stripe Connect account status, or `null` when onboarding has never been started.
 * GET /payments/connect/account 404s before the Express account exists — we map that single case to
 * `null` (a legible "not connected yet" state) and rethrow anything else.
 */
export function useConnectAccount() {
  return useQuery({
    queryKey: ik.connectAccount,
    queryFn: async (): Promise<ConnectedAccount | null> => {
      const res = await api.GET('/v1/payments/connect/account');
      if (res.response.status === 404) return null;
      return unwrap(res);
    },
    // A missing account is a real "not connected" answer, not a transient failure — don't retry it.
    retry: (count, error) => {
      if (error instanceof ApiClientError && error.status >= 400 && error.status < 500)
        return false;
      return count < 2;
    },
  });
}

/**
 * Create the Connect Express account if it doesn't exist yet (returns the existing one otherwise).
 * The hosted onboarding link is minted separately by useAccountLink — this just guarantees the
 * account row exists so a link can be created against it.
 */
export function useCreateConnectAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ConnectedAccount> =>
      unwrap(await api.POST('/v1/payments/connect/account')),
    onSuccess: () => qc.invalidateQueries({ queryKey: ik.connectAccount }),
  });
}

/**
 * Mint a fresh Stripe hosted onboarding / KYC account link (re-onboarding). Single-use, short-lived;
 * the caller opens `result.url` in a new tab. returnPath/refreshPath default server-side to the
 * settings payments screen.
 */
export function useAccountLink() {
  return useMutation({
    mutationFn: async (body: CreateAccountLink = {}): Promise<AccountLink> =>
      unwrap(await api.POST('/v1/payments/connect/account-link', { body })),
  });
}

/**
 * Comms provider health — the SMS/WhatsApp allowance meter for the current period. Email + push are
 * always free and unmetered; this is the paid-channel budget, with what smart-routing saved by
 * preferring free channels. Used as the "comms provider health" tile in the developer panel.
 */
export function useCommsAllowance() {
  return useQuery({
    queryKey: ik.commsAllowance,
    queryFn: async (): Promise<CommsAllowance> => unwrap(await api.GET('/v1/comms/allowance')),
  });
}
